import { getState } from '@redux-src/state';
import { isImmutable } from 'immutable';
import { get } from 'lodash';
import { connect as _connect } from 'react-redux';

/**
 * Map state props based on string path or function that receive state as parameter.
 * @example
 *  // Assuming our reducer 'Theme' contain Immutable.js Data
 *  const props = mapProps({
 *      fontFamily: 'Theme.fontFamily',
 *      colors (state) {
 *          return state.Theme.get('colors');
 *      }
 *  })
 *
 *  // Will return a function that can accept a state and return an object of type Record<string, any>
 * @param props
 * @return {mixed}
 */
export function mapProps(props = {}) {
    if (typeof props === 'function') {
        return props;
    }

    return (state, ...rest) =>
        Object.keys(props).reduce((bag, key) => {
            let value = props[key];
            if (!value) {
                return bag;
            }

            if (typeof value === 'string') {
                value = value.split('.');

                if (value.length === 0) {
                    return bag;
                }

                bag[key] = state[value[0]] || [];

                const path = value.slice(1);

                if (isImmutable(bag[key])) {
                    bag[key] = bag[key].getIn(path);
                } else {
                    bag[key] = get(bag[key], path.join('.'));
                }
            } else if (typeof value === 'function') {
                bag[key] = value(state, ...rest);
            } else if (typeof value === 'object' && !Array.isArray(value)) {
                bag[key] = mapProps(value)(state, ...rest);
            } else {
                bag[key] = value;
            }

            return bag;
        }, {});
}

/**
 * Map actions props based on function that will dispatch an action to the store.
 * @example
 * // Where loginAction it's defined on Redux/source/Auth/actions.js
 * const dispatchers = mapDispatchers({
 *     loginAction
 * })
 *
 * // Will return a function that accept a `dispatch` argument and return an object of type Record<string, () => any>
 * @param dispatchers
 * @returns {mixed}
 */
export function mapDispatchers(dispatchers = {}) {
    if (typeof dispatchers === 'function') {
        return dispatchers;
    }

    return (dispatch) =>
        Object.keys(dispatchers).reduce((bag, key) => {
            const value = dispatchers[key];

            if (value) {
                if (typeof value === 'string') {
                    bag[key] = (payload) => dispatch({ type: value, payload });
                } else if (typeof value === 'object' && value.type) {
                    bag[key] = () => dispatch(value);
                } else if (typeof value === 'function') {
                    bag[key] = (...args) => dispatch(value(...args, dispatch, getState()));
                }
            }

            return bag;
        }, {});
}

/**
 * Custom connect
 * @example
 * // Before
 * connect((state) => ({
 *     authenticated: state.Auth.get('authenticated')
 * }), (dispatch) => ({
 *     doSomething: (data) => {
 *         dispatch(doSomething(data));
 *     }
 * }))
 * // After
 * connect({
 *     authenticated: 'Auth.authenticated'
 * }, {
 *     doSomething
 * })
 * @param props
 * @param dispatchers
 * @return {mixed}
 */
export function connect(props?: any, dispatchers?: any): any {
    const props$ = props ? mapProps(props) : props;
    const dispatchers$ = dispatchers ? mapDispatchers(dispatchers) : dispatchers;

    return _connect(props$, dispatchers$);
}

export default connect;
